import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Col, Form, Row, Select} from "antd";
import {FormattedMessage} from "react-intl";
import {WidgetLocation, WidgetLocationOptions} from "@models/widget";
import PersonSelection from "../people/Selection";
import {Person} from "@models/person";
import {Category} from "@models/category";
import {Tag} from "@models/tag";
import CategorySelection from "@components/categories/Selection";
import TagSelection from "@components/tags/Selection";
import tagApi from "@api/tag";
import categoryApi from "@api/category";
import personApi from "@api/person";
import {Platform, PlatformOptions, SelectionMode} from "@models/index";

const Option = Select.Option;

type LocationSettingsShape = {
    owner: Platform;
    location: WidgetLocation;
    location_reference: string;
    form: any;
};


function LocationSettings(props: LocationSettingsShape, ref) {
    const {form} = props;
    const [settings, setSettings] = useState({
        location_reference: null,
        owner: props.owner || null,
        location: props.location || WidgetLocation.HOME_PAGE
    });

    // TODO:
    useEffect(() => {
        if (typeof props.location_reference == "string") {
            // fetchRefrenceItem(props.location_reference);
        }
    }, [props.location_reference]);


    /**
     * This function returns suitable api base of location type
     * Server does not return reference location object and UI needs to fetch it to show in edit mode!
     */
    const getReferenceApi = () => {
        switch (settings.location) {
            case WidgetLocation.TAG:
                return tagApi;

            case WidgetLocation.CATEGORY:
                return categoryApi;

            case WidgetLocation.PERSON:
                return personApi
        }
    };

    const fetchRefrenceItem = (location_reference_id) => {
        getReferenceApi().getItem({
            params: {
                id: location_reference_id
            }
        })
            .then(({data}) => {
                setSettings({
                    ...settings,
                    location_reference: {
                        ...data,
                        item_type: settings.location
                    }
                })
            })
            .catch((error) => {
                console.log(error)
            })
    };

    useImperativeHandle(ref, () => ({
        getLocationSettings: () => {
            let location = null;
            props.form.validateFieldsAndScroll((err, values) => {
                if (!err) {
                    const {["location_reference"]: omit, ...rest} = values;
                    location = {
                        ...rest,
                        owner: values.owner ? values.owner.join(",") : null,
                        location_reference: omit ? omit.id : null
                    }
                }
            });
            return location
        }
    }));

    const setLocationReference = (obj: Person | Category | Tag) => {
        setSettings({
            ...settings,
            location_reference: obj.id
        })
    };

    const getLocationrefrenceSelection = () => {
        const {getFieldDecorator} = form;
        const location = form.getFieldValue("location");

        switch (location) {
            case WidgetLocation.PERSON:
                const selectedPerson = settings.location_reference && settings.location_reference.item_type == WidgetLocation.PERSON ? settings.location_reference : null;
                return (
                    <Form.Item label={<FormattedMessage id="app.select_person" defaultMessage={`Person`}/>}>
                        {getFieldDecorator("location_reference", {
                            initialValue: selectedPerson,
                            rules: [{
                                required: true,
                                message: (
                                    <FormattedMessage
                                        id={`app.person_is_required`}
                                        defaultMessage={`Person is required!`}
                                    />
                                )
                            }]
                        })(
                            <PersonSelection onChange={setLocationReference}
                                             selectedPerson={selectedPerson}/>
                        )}
                    </Form.Item>
                );

            case WidgetLocation.CATEGORY:
                const selectedCategory = settings.location_reference && settings.location_reference.item_type == WidgetLocation.CATEGORY ? settings.location_reference : null;
                return (
                    <Form.Item label={<FormattedMessage id="app.select_category" defaultMessage={`Category`}/>}>
                        {getFieldDecorator("location_reference", {
                            initialValue: settings.location_reference,
                            rules: [{
                                required: true,
                                message: (
                                    <FormattedMessage
                                        id={`app.category_is_required`}
                                        defaultMessage={`Category is required!`}
                                    />
                                )
                            }]
                        })(
                            <CategorySelection onChange={setLocationReference}
                                               mode={SelectionMode.DEFAULT}
                                               initialValue={selectedCategory}/>
                        )}
                    </Form.Item>
                );

            case WidgetLocation.TAG:
                const selectedTag = settings.location_reference && settings.location_reference.item_type == WidgetLocation.TAG ? settings.location_reference : null;
                return (
                    <Form.Item label={<FormattedMessage id="app.select_tag" defaultMessage={`Tag`}/>}>
                        {getFieldDecorator("location_reference", {
                            initialValue: settings.location_reference,
                            rules: [{
                                required: true,
                                message: (
                                    <FormattedMessage
                                        id={`app.tag_is_required`}
                                        defaultMessage={`Tag is required!`}
                                    />
                                )
                            }]
                        })(
                            <TagSelection onChange={setLocationReference}
                                          mode={SelectionMode.DEFAULT}
                                          initialValue={selectedTag}/>
                        )}
                    </Form.Item>
                );

            default:
                return null;
        }
    };

    const onChangeLocation = () => {
        setSettings({
            ...settings,
            location_reference: null
        })
    };

    return (
        <div className={`widget-location-settings`}>
            <Form>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="app.select_platform" defaultMessage={`Select Platform`}/>}>
                            {form.getFieldDecorator("owner", {
                                initialValue: !!settings.owner ? settings.owner.replace(/\s/g, "").split(',') : [],
                                rules: [{
                                    required: true,
                                    message: <FormattedMessage id="app.display_location_is_required"
                                                               defaultMessage={`Display location is required`}/>
                                }]
                            })(
                                <Select mode={SelectionMode.MULTIPLE} showArrow={true}>
                                    {PlatformOptions.map((opt, index) => (
                                        <Option key={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="app.widget_location" defaultMessage={`Widget Location`}/>}>
                            {form.getFieldDecorator("location", {
                                initialValue: settings.location,
                            })(
                                <Select onChange={(e) => onChangeLocation(e)} showArrow={true}>
                                    {WidgetLocationOptions.map((opt, index) => (
                                        (opt.value !== 'android_channel') && <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {getLocationrefrenceSelection()}
                    </Col>
                </Row>
            </Form>
        </div>
    )

}


export default Form.create({name: "widget-location-settings-form"})(forwardRef(LocationSettings));
